import React, { useState } from 'react';
import { icons } from '@ublend-npm/aulaui-next';
import { Element, Transforms } from 'slate';
import copy from 'copy-to-clipboard';

import {
  ReactEditor,
  useReadOnly,
  useSelected,
  useSlateStatic,
} from 'slate-react';
import {
  BlockLinkContent,
  BlockLinkWrapper,
  Content,
  ContentWrapper,
  Description,
  Header,
  BibliULinkWrapper,
  LoadingContainer,
  Title,
  Url,
} from './BibliULink.styled';

import { useAulaEditorContext } from '../../EditorContext';
import { Loading } from '../Loading/Loading';
import { BlockToolbar, BlockToolbarItem } from '../BlockToolbar/BlockToolbar';
import {
  BlockButton,
  Failed,
  FailedDescription,
  FailedIcon,
} from '../File/File.styled';
import { BibliUEntity } from '../../plugins/bibliU/bibliUType';
import { requestHandler } from '../../utils/api';

const title = 'BibliU - Reader';
const description = 'Launch the BibliU Reader';
const ltiEndpoint = 'bibliu-lti-consumer';

export type BibliULinkProps = {
  element: Element;
};

export const BibliULink = ({ element }: BibliULinkProps) => {
  const editor = useSlateStatic();
  const isReadOnly = useReadOnly();
  const isSelected = useSelected();

  const { entities, config, updateEntity } = useAulaEditorContext();
  const [hovering, setHovering] = useState(false);
  const [copyTooltip, setCopyTooltip] = useState('Copy');
  const [loading, setLoading] = useState(false);

  // validate entity
  if (element.entity == null) return null;
  const { entity: entityId } = element;
  const selectedEntity = entities[entityId];
  if (selectedEntity?.type !== 'lti') return null;

  const { url, failed } = selectedEntity as BibliUEntity;
  const { spaceId, userId } = config;
  const { BibliU: BibliUIcon, Delete, CopyLink, OpenInNew } = icons;

  const onDelete = () => {
    Transforms.delete(editor, {
      at: ReactEditor.findPath(editor, element),
      voids: true,
    });
    ReactEditor.focus(editor);
  };

  const onCopy = () => {
    ReactEditor.focus(editor);
    if (!ReactEditor.isReadOnly(editor)) {
      setTimeout(() => {
        const path = ReactEditor.findPath(editor, element);
        Transforms.select(editor, path);
      });
    }
  };

  /**
   * Obtains the lti request data from bibliu-lti-consumer lambda,
   */
  const getLtiReq = async (ltiUrl: string) => {
    try {
      setLoading(true);
      const { data } = await requestHandler({
        url: ltiUrl,
        method: 'post',
        userId,
        data: {
          spaceId,
          launchUrl: url,
        },
      });

      setLoading(false);

      // if there is an error message in payload,
      //  then mark the entity as failed
      if (data?.errors?.length > 0) {
        console.error(data.errors[0]);
        updateEntity(entityId, { failed: true });
        return null;
      }

      if (data.message) {
        console.error(data.message);
        updateEntity(entityId, { failed: true });
        return null;
      }

      return data;
    } catch (ex) {
      setLoading(false);
      console.error(ex);
      updateEntity(entityId, { failed: true });
      return null;
    }
  };

  /**
   * 1 - Obtains the lti request data via getLtiReq
   * 2 - Constructs the authenticated URL to bibliu.com so that login page is bypassed
   * 3 - Returns the authenticated URL
   */
  const getLaunchFormUrl = async () => {
    // obtain lti provider request
    const ltiUrl = `${config.apiUrl}/${ltiEndpoint}`;
    const request = await getLtiReq(ltiUrl);
    if (!request) {
      return null;
    }

    const { body, url: ltiLaunchUrl } = request;
    const bodyEncoded = encodeURIComponent(JSON.stringify(body));
    const urlEncoded = encodeURIComponent(ltiLaunchUrl);

    // construct authenticated URL
    const launchFormUrlBase = `${config.apiUrl}/lti-launchform`;
    return `${launchFormUrlBase}?body=${bodyEncoded}&url=${urlEncoded}`;
  };

  /**
   * Obtains the authenticated URL to bibliu.com and opens the URL in a new tab
   */
  const onClick = async () => {
    const launchFormUrl = await getLaunchFormUrl();
    if (launchFormUrl) {
      window.open(launchFormUrl);
    }
  };

  const handleCopyLink = (e: React.SyntheticEvent) => {
    e.preventDefault();
    copy(url);
    setCopyTooltip('Copied');
    setTimeout(() => {
      setCopyTooltip('Copy link');
    }, 1000);
    onCopy();
  };

  if (loading) {
    return (
      <LoadingContainer contentEditable={false}>
        <Loading />
      </LoadingContainer>
    );
  }

  const toolbarItems: BlockToolbarItem[] = [
    {
      title: 'Delete',
      isVisible: !isReadOnly,
      icon: Delete,
      onAction: onDelete,
    },
    {
      title: copyTooltip,
      icon: CopyLink,
      onAction: (e) => handleCopyLink(e),
    },
    {
      title: 'Open in new tab',
      label: 'Open',
      icon: OpenInNew,
      onAction: onClick,
    },
  ];

  const blockToolbar = (
    <BlockToolbar isVisible={isSelected || hovering} items={toolbarItems} />
  );

  if (failed) {
    const onRetry = () => updateEntity(entityId, { failed: false });
    return (
      <Failed
        isSelected={isSelected}
        data-testid="failed-bibliu-link"
        contentEditable={false}
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        aria-current={isSelected}
      >
        {blockToolbar}
        <FailedIcon>
          <icons.ErrorIcon />
        </FailedIcon>
        <FailedDescription>
          {title} content failed to load.
          {!isReadOnly && (
            <>
              <br />
              <BlockButton onClick={onRetry}>Retry</BlockButton>
            </>
          )}
        </FailedDescription>
      </Failed>
    );
  }

  return (
    <BibliULinkWrapper
      onClick={onClick}
      tabIndex={-1}
      aria-label={title}
      data-testid="bibliu-link"
      isSelected={isSelected}
      aria-current={isSelected}
      contentEditable={false}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
    >
      <BlockLinkWrapper contentEditable={false}>
        <BlockLinkContent>
          <Header>
            <BibliUIcon />
            <Title>{title}</Title>
          </Header>
          {blockToolbar}
          <ContentWrapper>
            <Content hasIcon={false}>
              <Description>{description}</Description>
              <Url>{url}</Url>
            </Content>
          </ContentWrapper>
        </BlockLinkContent>
      </BlockLinkWrapper>
    </BibliULinkWrapper>
  );
};
